import { useRef } from 'react';
import {
  useAchievementTypes,
  useDateNow,
  useDispatch,
  useReduxState
} from 'hooks';
import { addMonths } from 'date-fns';
import api from 'api';
import { Alert, ModalButtonWithForm, User } from 'components/common';
import {
  Datepicker,
  FormGroup,
  MultipleAttachments,
  TextArea
} from 'components/common/form';
import { ProductUserCustomAttributeRenderFormField } from 'components/productUser';

import type { FormApi } from 'informed';
import type { UserWithBasicData } from 'shared-types';

type FormValues = {
  completionDate: number;
  expirationDate: number;
  comments: string;
  startedAtDate: number;
};

interface Props {
  user: UserWithBasicData;
  userHasPreviousCompletions?: boolean;
  updateUsers: () => Promise<void>;
}

const AchievementCompletionsAddUser = ({
  user,
  userHasPreviousCompletions,
  updateUsers
}: Props) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { achievement }
  } = useReduxState();

  const { hasCompletion, hasExpiration, hasStartedAt } = useAchievementTypes({
    type: achievement?.type
  });

  const formApi = useRef<FormApi<FormValues>>(undefined);
  const userUploader = useRef<any>(undefined);

  const handleCompletionDateChange = () => {
    const { completionDate } = formApi.current.getState().values;

    if (achievement.monthsBeforeExpiration)
      formApi.current.setValue(
        'expirationDate',
        completionDate
          ? addMonths(completionDate, achievement.monthsBeforeExpiration)
          : null
      );
  };

  const submit = async (values: FormValues) => {
    try {
      const attachments = await userUploader.current();

      const userAttributes = {};

      Object.keys(values).forEach((fieldKey) => {
        if (fieldKey.startsWith('userAttributes-')) {
          const attributeId = fieldKey.split('-')[1];
          userAttributes[attributeId] = values[fieldKey];
        }
      });

      const achievementCompletions = [
        {
          attachments,
          comments: values.comments,
          completionDate: values.completionDate,
          expirationDate: values.expirationDate,
          startedAtDate: values.startedAtDate,
          status: ['external', 'milestone'].includes(achievement.type)
            ? 'completed'
            : 'inprogress',
          userAttributes,
          ...(user.email ? { email: user.email } : { username: user.username })
        }
      ];

      const response = await api.post(
        `/admin/achievements/${achievement.id}/completions`,
        { achievementCompletions }
      );

      const successMessage =
        `${response.data.addedUsers} ${messages.settingsAddUsersUsersAdded}, ` +
        `${response.data.newUsers} ${messages.settingsAddUsersAccountsCreated}`;

      await updateUsers();
      dispatch.addNotice({ text: successMessage, type: 'success' });
    } catch (err) {
      if (err.message === messages.commonUploadFileFailed) {
        return false; // keep the modal open, user already notified by the uploader
      } else {
        await dispatch.handleErrorDispatch(err);
      }
    }
  };

  const initialValues = {
    completionDate: dateNow,
    expirationDate: achievement.monthsBeforeExpiration
      ? addMonths(dateNow, achievement.monthsBeforeExpiration)
      : null,
    startedAtDate: null
  };

  return (
    <>
      <ModalButtonWithForm
        buttonCategory="lighter"
        buttonIcon="plus"
        buttonSize="header"
        initialValues={initialValues}
        modalActionCategory="success"
        modalActionText={messages.commonAdd}
        modalWidth={hasStartedAt ? 'medium' : 'small'}
        onSubmit={submit}
        setFormApi={(form: FormApi<FormValues>) => (formApi.current = form)}
        {...(userHasPreviousCompletions
          ? {
              buttonIsDropdown: true,
              buttonText: messages.commonAddNewCompletion,
              modalTitle: messages.commonAddNewCompletion
            }
          : {
              buttonProps: {
                tooltip:
                  achievement.type === 'tasklist'
                    ? messages.achievementTaskListAddTo
                    : achievement.type === 'conversation'
                    ? messages.achievementConversationNotesAddTo
                    : messages.commonAddCompletion
              },
              modalTitle:
                achievement.type === 'tasklist'
                  ? messages.achievementTaskListAddTo
                  : achievement.type === 'conversation'
                  ? messages.achievementConversationNotesAddTo
                  : messages.commonAddCompletion
            })}
      >
        {() => (
          <>
            {userHasPreviousCompletions ? (
              <Alert
                style={{ marginBottom: 30 }}
                text={messages.achievementAddArchiveInfo}
              />
            ) : null}

            <div style={{ marginBottom: 30 }}>
              <User user={user} />
            </div>

            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between'
              }}
            >
              {hasStartedAt ? (
                <FormGroup label={messages.commonStartDate}>
                  <Datepicker
                    field="startedAtDate"
                    id={`${user.email || user.username}-started-at-date`}
                    isClearable={true}
                    placeholder={messages.publishStartDateEmpty}
                    maxDate={
                      formApi.current.getState().values?.completionDate ||
                      formApi.current.getState().values?.expirationDate
                    }
                    simpleClear={true}
                    style={{ width: 170 }}
                  />
                </FormGroup>
              ) : null}

              {hasCompletion ? (
                <FormGroup label={messages.completionsCompletionDate}>
                  <Datepicker
                    field="completionDate"
                    id={`${user.email || user.username}-completion-date`}
                    isClearable={true}
                    placeholder={messages.publishStartDateEmpty}
                    maxDate={formApi.current.getState().values?.expirationDate}
                    minDate={formApi.current.getState().values?.startedAtDate}
                    onChange={handleCompletionDateChange}
                    simpleClear={true}
                    style={{ width: 170 }}
                  />
                </FormGroup>
              ) : null}

              {hasExpiration ? (
                <FormGroup label={messages.commonExpirationDate}>
                  <div
                    {...(achievement.monthsBeforeExpiration && {
                      'aria-label':
                        messages.achievementCompletionAutomaticExpirationTooltip,
                      'data-tooltip':
                        messages.achievementCompletionAutomaticExpirationTooltip
                    })}
                  >
                    <Datepicker
                      disabled={achievement.monthsBeforeExpiration}
                      field="expirationDate"
                      id={`${user.email || user.username}-expiration-date`}
                      isClearable={true}
                      placeholder={messages.publishEndDateEmpty}
                      minDate={
                        formApi.current.getState().values?.completionDate ||
                        formApi.current.getState().values?.startedAtDate
                      }
                      simpleClear={true}
                      style={{ width: 170 }}
                    />
                  </div>
                </FormGroup>
              ) : null}
            </div>

            <FormGroup label={messages.commonComment} maxWidth="100%">
              <TextArea field="comments" id={`${user.id}-comments`} rows={5} />
            </FormGroup>

            <MultipleAttachments
              getUploadHandler={(fn) => (userUploader.current = fn)}
            />

            {achievement.userAttributes?.map((attribute) => (
              <FormGroup
                key={attribute.id}
                label={
                  attribute.titleTranslations?.[language] || attribute.title
                }
                labelIsOptional={!attribute.isRequired}
              >
                <ProductUserCustomAttributeRenderFormField
                  attribute={attribute}
                />
              </FormGroup>
            ))}
          </>
        )}
      </ModalButtonWithForm>
    </>
  );
};

export default AchievementCompletionsAddUser;
